import React, { useState, useEffect } from "react";
import Select from "react-select";
import "../../css/Discord/LinkDiscordUserModal.css";
import getParticipantStatusStyle from "../../../utils/getParticipantStatusStyle";
import { useToast } from "../../../Contexts/ToastContext";

const normalize = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[ąćęłńóśźż]/g, (c) =>
      ({ ą: "a", ć: "c", ę: "e", ł: "l", ń: "n", ó: "o", ś: "s", ź: "z", ż: "z" }[c])
    )
    .replace(/[ĄĆĘŁŃÓŚŹŻ]/g, (c) =>
      ({ Ą: "A", Ć: "C", Ę: "E", Ł: "L", Ń: "N", Ó: "O", Ś: "S", Ź: "Z", Ż: "Z" }[c])
    )
    .toLowerCase()
    .trim();

const DiscordLinksModal = ({
  isOpen,
  onClose,
  participants,
  discordUsers,
  onSave,
  onRevokeAbsent,
}) => {
  const [rows, setRows] = useState([]);
  const [isAutoMatching, setIsAutoMatching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [newAutoMatches, setNewAutoMatches] = useState([]);
  const [ambiguousMatches, setAmbiguousMatches] = useState([]);
  const [hasTriedAutoMatch, setHasTriedAutoMatch] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (isOpen) {
      const initialRows = participants.map((p) => ({
        participantId: p.participantId,
        participantName: p.participantName,
        discordUserId: p.discordUserId || null,
        discordDisplayName: p.discordDisplayName || null,
        status: p.status,
        isActive: p.isActive,
      }));

      setRows(initialRows);
      setNewAutoMatches([]);
      setAmbiguousMatches([]);
      setHasTriedAutoMatch(false);
    }
  }, [isOpen, participants]);

  const handleChange = (participantId, discordUserId) => {
    const matchedUser = discordUsers.find((u) => u.id === discordUserId);

    setRows((prev) =>
      prev.map((r) =>
        r.participantId === participantId
          ? {
              ...r,
              discordUserId,
              discordDisplayName: matchedUser?.displayName || null,
            }
          : r
      )
    );
  };

  const handleAutoMatch = () => {
    setIsAutoMatching(true);
    setHasTriedAutoMatch(true);

    const usedDiscordIds = new Set(
      rows.filter((r) => r.discordUserId).map((r) => r.discordUserId)
    );

    const matches = [];
    const ambiguous = [];

    const updated = rows.map((r) => {
      if (r.isActive === false) return r;
      if (r.discordUserId) return r;

      const normName = normalize(r.participantName);

      const possibleMatches = discordUsers.filter((u) => {
        if (!u?.id) return false;
        if (usedDiscordIds.has(u.id)) return false;
        return normalize(u.displayName) === normName;
      });

      if (possibleMatches.length === 0) {
        return r;
      }

      if (possibleMatches.length > 1) {
        ambiguous.push({
          participantId: r.participantId,
          participantName: r.participantName,
          status: r.status,
          isActive: r.isActive,
          selectedDiscordUserId: r.discordUserId || null,
          candidates: possibleMatches.map((u) => ({
            discordUserId: u.id,
            displayName: u.displayName,
            userName: u.username,
            avatarUrl: u.avatarUrl,
          })),
        });

        return r;
      }

      const found = possibleMatches[0];
      usedDiscordIds.add(found.id);

      matches.push({
        participantId: r.participantId,
      });

      return {
        ...r,
        discordUserId: found.id,
        discordDisplayName: found.displayName,
      };
    });

    setRows(updated);
    setNewAutoMatches((prev) => {
        const mergedIds = new Set([
          ...prev.map((m) => m.participantId),
          ...matches.map((m) => m.participantId),
        ]);

        return Array.from(mergedIds).map((participantId) => ({ participantId }));
      });
    setAmbiguousMatches(ambiguous);

    setTimeout(() => setIsAutoMatching(false), 300);
  };

  const handleAmbiguousCandidateClick = (participantId, discordUserId) => {
    handleChange(participantId, discordUserId);

    setAmbiguousMatches((prev) =>
      prev.map((m) =>
        m.participantId === participantId
          ? { ...m, selectedDiscordUserId: discordUserId }
          : m
      )
    );
  };

  const handleSave = async () => {
    const changed = rows.filter((r) => {
      const original = participants.find((p) => p.participantId === r.participantId);
      return (original?.discordUserId || null) !== (r.discordUserId || null);
    });

    if (!changed.length) {
      onClose();
      return;
    }

    const payload = changed.map((r) => ({
      participantId: r.participantId,
      discordUserId: r.discordUserId || "0",
    }));

    try {
      setIsSaving(true);
      await onSave(payload);
    } finally {
      setIsSaving(false);
    }
  };

  const autoMatchedIds = new Set(newAutoMatches.map((m) => m.participantId));
  const ambiguousIds = new Set(ambiguousMatches.map((m) => m.participantId));

  const autoMatchedRows = rows.filter((r) => autoMatchedIds.has(r.participantId));

  const remainingRows = rows.filter(
    (r) => !autoMatchedIds.has(r.participantId) && !ambiguousIds.has(r.participantId)
  );

  if (!isOpen) return null;

  const discordOptions = [...discordUsers]
    .sort((a, b) =>
      (a.displayName || "").localeCompare(b.displayName || "", "pl", {
        sensitivity: "base",
      })
    )
    .map((u) => ({
      value: u.id,
      label: u.displayName,
      username: u.username,
      avatar: u.avatarUrl,
    }));

  const isMobile = window.innerWidth <= 768;

  const formatOptionLabel = (option) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, lineHeight: 1 }}>
      {option.avatar && (
        <img
          src={option.avatar}
          alt={option.label}
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            objectFit: "cover",
            marginTop: -1,
          }}
        />
      )}
      {isMobile ? (
        <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{option.label}</span>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: "0.85rem",
          }}
        >
          <span style={{ fontWeight: 500 }}>{option.label}</span>
          <span
            style={{
              color: "#777",
              fontSize: "0.8rem",
              position: "relative",
              top: "-1px",
            }}
          >
            @{option.username}
          </span>
        </div>
      )}
    </div>
  );

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "30px",
      height: "30px",
      borderRadius: "8px",
      fontSize: "0.85rem",
      padding: "0 4px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 4px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "28px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999999,
    }),
  };

  const renderParticipantRow = (r, isHighlighted = false) => {
    const isDisabled = r.isActive === false;
    const statusStyle = getParticipantStatusStyle(r.status);

    return (
      <div
        key={r.participantId}
        className={`ldum-user-item ${isDisabled ? "ldum-user-disabled" : ""} ${
          isHighlighted ? "ldum-user-item-highlighted" : ""
        }`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <div className="dc-user-left" style={{ flex: 1 }}>
          <i
            className={`fa ${statusStyle.icon} dc-user-icon ${statusStyle.statusClass}`}
            title={r.status}
          />
          <span
            className={`dc-user-name-link ${
              statusStyle.strikeThrough ? "dc-strikethrough" : ""
            }`}
          >
            {r.participantName}
          </span>
        </div>

        <div style={{ width: "60%" }}>
          <Select
            isDisabled={isDisabled}
            options={discordOptions}
            value={discordOptions.find((o) => o.value === r.discordUserId) || null}
            onChange={(opt) => handleChange(r.participantId, opt?.value || null)}
            placeholder={isDisabled ? " - - -" : "Wybierz użytkownix"}
            isClearable
            formatOptionLabel={formatOptionLabel}
            menuPortalTarget={document.body}
            menuShouldScrollIntoView={false}
            menuPosition="fixed"
            styles={selectStyles}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="ldum-overlay">
      <div
        className="ldum-modal"
        style={{
          width: 650,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <p className="ldum-subtitle">
            🔗 Przyporządkuj konta Discord uczestnikom tego wyjazdu:
          </p>

          <div className="ldum-header-actions">
            <div className="ldum-buttons">
              <div
                className="ldum-btn ldum-btn-auto"
                onClick={handleAutoMatch}
                disabled={isAutoMatching || isSaving || isRevoking}
              >
                {isAutoMatching ? (
                  <>
                    <i className="fa fa-spinner fa-spin" /> Dopasowywanie...
                  </>
                ) : (
                  <>🤖 Powiąż automatycznie</>
                )}
              </div>

              <div
                className="ldum-btn ldum-btn-revoke"
                onClick={async () => {
                  try {
                    setIsRevoking(true);
                    await onRevokeAbsent();
                  } catch (err) {
                    showToast("Wystąpił błąd podczas odbierania dostępu ⚠️", "error");
                  } finally {
                    setIsRevoking(false);
                  }
                }}
                disabled={isSaving || isAutoMatching || isRevoking}
              >
                {isRevoking ? (
                  <>
                    <i className="fa fa-spinner fa-spin" /> Odbieranie...
                  </>
                ) : (
                  <>🧹 Odbierz dostęp nieobecnym</>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className="ldum-user-list"
          style={{
            flexGrow: 1,
            overflowY: "auto",
            minHeight: 100,
            paddingRight: 4,
            opacity: isSaving ? 0.5 : 1,
            pointerEvents: isSaving ? "none" : "auto",
          }}
        >
          {ambiguousMatches.length > 0 && (
            <div className="ldum-section-block">
              <div className="ldum-section-title ldum-section-title-warning">
                ⚠️ Niejednoznaczne dopasowania ({ambiguousMatches.length})
              </div>
              <div className="ldum-section-subtitle">
                Dla tych osób znaleziono więcej niż jedno możliwe konto Discord. Spróbuj to zweryfikować z osobą lub najlepiej poczekaj na automatyczne dołączenie do kanału przez tę osobę.
              </div>

              {ambiguousMatches.map((r) => {
                const isDisabled = r.isActive === false;
                const statusStyle = getParticipantStatusStyle(r.status);

                return (
                  <div
                    key={r.participantId}
                    className={`ldum-user-item ${
                      isDisabled ? "ldum-user-disabled" : ""
                    } ldum-user-item-warning`}
                    style={{
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className="dc-user-left" style={{ flex: 1 }}>
                        <i
                          className={`fa ${statusStyle.icon} dc-user-icon ${statusStyle.statusClass}`}
                          title={r.status}
                        />
                        <span
                          className={`dc-user-name-link ${
                            statusStyle.strikeThrough ? "dc-strikethrough" : ""
                          }`}
                        >
                          {r.participantName}
                        </span>
                      </div>

                      <div style={{ width: "60%" }}>
                        <Select
                          isDisabled={isDisabled}
                          options={discordOptions}
                          value={
                            discordOptions.find((o) => o.value === r.selectedDiscordUserId) ||
                            discordOptions.find((o) => o.value === r.discordUserId) ||
                            null
                          }
                          onChange={(opt) =>
                            handleAmbiguousCandidateClick(
                              r.participantId,
                              opt?.value || null
                            )
                          }
                          placeholder={isDisabled ? " - - -" : "Wybierz użytkownix"}
                          isClearable
                          formatOptionLabel={formatOptionLabel}
                          menuPortalTarget={document.body}
                          menuShouldScrollIntoView={false}
                          menuPosition="fixed"
                          styles={selectStyles}
                        />
                      </div>
                    </div>

                    <div className="ldum-ambiguous-candidates">
                      Możliwe konta:
                      <div className="ldum-ambiguous-candidates-list">
                        {r.candidates.map((candidate) => {
                          const isSelected =
                            r.selectedDiscordUserId === candidate.discordUserId;

                          return (
                            <div
                              key={candidate.discordUserId}
                              className={`ldum-ambiguous-candidate ${
                                isSelected ? "ldum-ambiguous-candidate-selected" : ""
                              } ${
                                isDisabled || isSaving || isAutoMatching || isRevoking
                                  ? "ldum-ambiguous-candidate-disabled"
                                  : ""
                              }`}
                              onClick={() => {
                                if (
                                  isDisabled ||
                                  isSaving ||
                                  isAutoMatching ||
                                  isRevoking
                                )
                                  return;

                                handleAmbiguousCandidateClick(
                                  r.participantId,
                                  candidate.discordUserId
                                );
                              }}
                              title={`Wybierz ${candidate.displayName}`}
                            >
                              {candidate.avatarUrl && (
                                <img
                                  src={candidate.avatarUrl}
                                  alt={candidate.displayName}
                                  className="ldum-ambiguous-avatar"
                                />
                              )}
                              <span className="ldum-ambiguous-name">
                                {candidate.displayName}
                              </span>
                              <span className="ldum-ambiguous-username">
                                @{candidate.userName}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="ldum-section-block">
            {newAutoMatches.length > 0 ? (
              <>
              <div className="ldum-section-title">
              ✨ Nowe dopasowania ({newAutoMatches.length})
            </div>
                <div className="ldum-section-subtitle">
                  To są powiązania znalezione podczas ostatniego automatycznego dopasowania.
                </div>
                {autoMatchedRows.map((r) => renderParticipantRow(r, true))}
              </>
            ) : hasTriedAutoMatch ? (
              <div className="ldum-empty-info">
                Brak nowych automatycznych dopasowań.
              </div>
            ) : (
              <div className="ldum-section-subtitle">
                Kliknij „Powiąż automatycznie”, aby spróbować znaleźć nowe dopasowania.
              </div>
            )}
          </div>

          <div className="ldum-section-block">
            <div className="ldum-section-title">
              {newAutoMatches.length > 0 || ambiguousMatches.length > 0
                ? "Pozostałe osoby"
                : "Wszyscy uczestnicy"}
            </div>

            {remainingRows.length === 0 ? (
              <div className="ldum-empty-info">Brak pozycji do wyświetlenia.</div>
            ) : (
              remainingRows.map((r) => renderParticipantRow(r))
            )}
          </div>
        </div>

        <div className="ldum-actions">
          <button
            onClick={onClose}
            className="ldum-cancel"
            disabled={isAutoMatching || isSaving || isRevoking}
          >
            Anuluj
          </button>
          <button
            onClick={handleSave}
            className="ldum-save"
            disabled={isAutoMatching || isSaving || isRevoking}
          >
            {isSaving ? (
              <>
                <span className="ldum-spinner" /> Zapisywanie...
              </>
            ) : (
              "💾 Zapisz wszystkie"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscordLinksModal;